import { useEffect, useState } from "react";
import { X, Sparkles } from "lucide-react";
import { fmtEur, fmtNum, fmtMwhc } from "@/lib/format";

const WELCOME_KEY = "cee_welcome";
export const markWelcome = () => sessionStorage.setItem(WELCOME_KEY, "1");
export const hasWelcome = () => !!sessionStorage.getItem(WELCOME_KEY);
export const clearWelcome = () => sessionStorage.removeItem(WELCOME_KEY);

const useCountUp = (target, delay) => {
  const [v, setV] = useState(0);
  useEffect(() => {
    let raf;
    const start = performance.now() + delay;
    const tick = (now) => {
      const t = Math.min(1, Math.max(0, (now - start) / 1400));
      setV(target * (1 - Math.pow(1 - t, 3)));
      if (t < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [target, delay]);
  return v;
};

const Figure = ({ label, target, format, delay, testid }) => {
  const v = useCountUp(target, delay);
  return (
    <div className="animate-fade-up" style={{ animationDelay: `${delay}ms` }}>
      <div className="eyebrow text-[10px] text-white/70">{label}</div>
      <div className="metric text-xl sm:text-2xl font-bold text-white mt-1 whitespace-nowrap" data-testid={testid}>{format(v)}</div>
    </div>
  );
};

export const WelcomeBanner = ({ name, space, stats, onClose }) => {
  const [leaving, setLeaving] = useState(false);
  const close = () => { setLeaving(true); setTimeout(onClose, 350); };

  useEffect(() => {
    const t = setTimeout(close, 9000);
    return () => clearTimeout(t);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className={`relative overflow-hidden rounded-2xl gradient-genis p-6 sm:p-8 shadow-[0_20px_60px_-20px_rgb(var(--genis)/0.6)] transition-[opacity,transform] duration-300 ${leaving ? "opacity-0 -translate-y-2" : "animate-fade-up"}`} data-testid="welcome-banner">
      <div className="absolute inset-0 grid-bg opacity-30" />
      <div className="absolute -right-16 -top-16 h-56 w-56 rounded-full bg-genis-accent/30 blur-3xl animate-float-slow" />
      <button onClick={close} aria-label="Fermer" className="absolute top-4 right-4 h-8 w-8 grid place-items-center rounded-full bg-white/15 text-white hover:bg-white/25 transition-colors" data-testid="welcome-banner-close">
        <X className="h-4 w-4" />
      </button>
      <div className="relative flex flex-col lg:flex-row lg:items-center gap-6 lg:gap-12">
        <div className="min-w-0 flex-1">
          <span className="inline-flex items-center gap-1.5 rounded-full bg-white/15 px-3 py-1 eyebrow text-[10px] text-white">
            <Sparkles className="h-3 w-3" /> {space}
          </span>
          <h2 className="mt-3 text-2xl sm:text-3xl font-bold text-white" data-testid="welcome-banner-title">Ravi de vous revoir, {name} !</h2>
          <p className="text-white/80 text-sm mt-1">Voici où en sont vos simulations CEE.</p>
        </div>
        <div className="grid grid-cols-3 gap-4 sm:gap-8 lg:ml-auto lg:pr-10 shrink-0">
          <Figure label="Dossiers" target={stats.total_projects || 0} format={(v) => fmtNum(Math.round(v))} delay={200} testid="welcome-figure-projects" />
          <Figure label="Volume" target={stats.total_kwhc || 0} format={fmtMwhc} delay={350} testid="welcome-figure-volume" />
          <Figure label="Prime totale" target={stats.total_prime || 0} format={(v) => fmtEur(Math.round(v))} delay={500} testid="welcome-figure-prime" />
        </div>
      </div>
      <div className="absolute bottom-0 left-0 h-0.5 bg-white/60 origin-left" style={{ width: "100%", animation: leaving ? "none" : "welcome-progress 9s linear forwards" }} />
    </div>
  );
};
